import type {Editor} from "@tiptap/core";
import type {TFunction} from "i18next";
import {
    DEFAULT_PRINTOUT_PARAGRAPH_VARIANT,
    normalizePrintoutParagraphVariant,
    PRINTOUT_PARAGRAPH_VARIANTS,
    type PrintoutParagraphVariant,
} from "../../blocks";

export type TextAlignment = 'left' | 'center' | 'right' | 'justify';
type ParagraphBlockFormat = PrintoutParagraphVariant;
export type BlockFormat = ParagraphBlockFormat | 'h1' | 'h2' | 'h3' | 'h4';
export type HeadingLevel = 1 | 2 | 3 | 4;
export type ListFormat = 'bullet' | 'ordered';

export const TEXT_ALIGNMENTS: TextAlignment[] = ['left', 'center', 'right', 'justify'];
export const HEADING_LEVELS: HeadingLevel[] = [1, 2, 3, 4];
export const BLOCK_FORMATS: BlockFormat[] = [...PRINTOUT_PARAGRAPH_VARIANTS, 'h1', 'h2', 'h3', 'h4'];
export const LIST_FORMATS: ListFormat[] = ['bullet', 'ordered'];
export const HEADING_LEVEL_BY_BLOCK_FORMAT: Partial<Record<BlockFormat, HeadingLevel>> = {
    h1: 1,
    h2: 2,
    h3: 3,
    h4: 4,
};

export const getAlignmentLabels = (t: TFunction): Record<TextAlignment, string> => ({
    left: t('Align left'),
    center: t('Align center'),
    right: t('Align right'),
    justify: t('Align justify'),
});

export const getBlockFormatLabels = (t: TFunction): Record<BlockFormat, string> => ({
    body1: t('Paragraph'),
    body2: t('Small text'),
    caption: t('Caption'),
    displayTitle: t('Display title'),
    largeValue: t('Large value'),
    detailValue: t('Detail value'),
    h1: t('Heading 1'),
    h2: t('Heading 2'),
    h3: t('Heading 3'),
    h4: t('Heading 4'),
});

export const getListFormatLabels = (t: TFunction): Record<ListFormat, string> => ({
    bullet: t('Bullet list'),
    ordered: t('Numbered list'),
});

export const getCurrentTextAlign = (editor: Editor): TextAlignment => {
    const activeAlignment = TEXT_ALIGNMENTS.find((alignment) => editor.isActive({textAlign: alignment}));

    return activeAlignment ?? 'left';
};

export const getCurrentBlockFormat = (editor: Editor): BlockFormat => {
    const activeHeadingLevel = HEADING_LEVELS.find((level) => editor.isActive('heading', {level}));

    if (activeHeadingLevel) {
        return `h${activeHeadingLevel}` as BlockFormat;
    }

    if (editor.isActive('paragraph')) {
        return normalizePrintoutParagraphVariant(editor.getAttributes('paragraph').variant);
    }

    return DEFAULT_PRINTOUT_PARAGRAPH_VARIANT;
};

export const isInList = (editor: Editor) => editor.isActive('bulletList') || editor.isActive('orderedList');

export const isHeadingBlockFormat = (format: BlockFormat) => Boolean(HEADING_LEVEL_BY_BLOCK_FORMAT[format]);

export const isParagraphBlockFormat = (format: BlockFormat): format is ParagraphBlockFormat => (
    PRINTOUT_PARAGRAPH_VARIANTS.includes(format as ParagraphBlockFormat)
);

export const toggleListFormat = (editor: Editor, listType: ListFormat) => {
    const chain = editor.chain().focus();

    if (isHeadingBlockFormat(getCurrentBlockFormat(editor))) {
        chain.setNode('paragraph', {variant: DEFAULT_PRINTOUT_PARAGRAPH_VARIANT});
    }

    if (listType === 'bullet') {
        chain.toggleBulletList().run();
        return;
    }

    chain.toggleOrderedList().run();
};
